import { useEffect, useId, useRef, useState } from 'react';
import { ANNOUNCEMENT } from '@/config/site';
import { FUEL_SURCHARGE_PERCENT } from '@/lib/shipping';
import { useI18n } from '@/client/i18n/I18nContext';

export function AnnouncementBar() {
  const { t } = useI18n();
  if (!ANNOUNCEMENT.active) return null;
  return (
    <div className="announcement" role="note">
      {t('announcement')}
      {FUEL_SURCHARGE_PERCENT > 0 && <FuelNotice />}
    </div>
  );
}

/**
 * Temporary fuel-surcharge notice (shown while FUEL_SURCHARGE_PERCENT > 0).
 * The ⓘ explanation opens on hover, keyboard focus and tap/click (touch
 * screens); leaving, blur, Escape or a tap outside closes it.
 */
function FuelNotice() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const tipId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  return (
    <span
      ref={ref}
      className="announcement__fuel"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {' · '}
      {t('announcement.fuel')}
      <button
        type="button"
        className="announcement__info"
        aria-label={t('announcement.fuelMore')}
        aria-expanded={open}
        aria-describedby={tipId}
        // Open, not toggle: on touch the tap also fires focus first.
        onClick={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        // Close when focus moves to another control (Tab), not when a tap on
        // the tooltip text blurs the button (relatedTarget null); taps outside
        // are handled by the pointerdown listener.
        onBlur={(e) => {
          if (e.relatedTarget) setOpen(false);
        }}
      >
        <span aria-hidden="true">i</span>
      </button>
      <span id={tipId} role="tooltip" className="announcement__tip" hidden={!open}>
        {t('announcement.fuelInfo')}
      </span>
    </span>
  );
}
